import { Dayts, createNotification, getClasses, saveFile } from '@/utils';
import { MouseEvent, ReactElement } from 'react';

import { AddonsTypes } from '@/features/Addons/types';
import Button from '@/common/Button';
import { DATETIME_FE_FORMAT_SHORT } from '@/constants';
import { Import } from '@/models/gen/graphql';
import Input from '@/common/Input';
import Table from '@/common/Table';
import Tippy from '@tippyjs/react';
import { Toast } from '@/models';
import { getAddonTableGridTemplateColumns } from './utils';
import runDownloadAddonImports from '@/features/Addons/api/services/runDownloadAddonImports';
import { useAddonsState } from '@/features/Addons';
import { useNavigate } from 'react-router-dom';

const AddonTableRow = ({
  id,
  createdAt,
  archivedAt,
  status,
  statusDisplayName,
  provider,
  importedBy,
  archivedByUser,
  type,
  email,
}: Import): ReactElement => {
  const navigate = useNavigate();
  const [{ isSelected, onSelect, handleDoubleClick, settings, archived, focused }] = useAddonsState(({ state, setState }) => [
    {
      isSelected: state.selected.includes(id),
      onSelect: (): void => state.onSelect(id),
      focused: state.focused.includes(id),
      handleDoubleClick: async (event: MouseEvent<HTMLTableRowElement>): Promise<void> => {
        event.preventDefault();
        event.stopPropagation();
        state.setFocus(id);
        if (!AddonsTypes.ReadyToImportStatuses.includes(status)) {
          setState((current) => ({
            ...current,
            modals: {
              ...current.modals,
              addonOverrides: { show: true, importId: id, name: current.imports.find((imp) => imp.id === id)?.name },
            },
          }));
          return; // don't navigate or open drawer until user confirms modal
        }
        if (settings.addonAsDrawer) return setState((current) => ({ ...current, expandedId: id }));
        navigate(`/manifests/${id}`);
      },
      settings: state.settings,
      archived: state.filters.archived,
    },
  ]);

  const onDownload = async (): Promise<void> => {
    try {
      const [res] = await runDownloadAddonImports(id);
      if (res?.error?.code || !res?.url) throw new Error(res.error?.message || 'Error downloading addon');
      saveFile(res?.url);
    } catch (err) {
      console.error('Error downloading addon:', err);
      createNotification(err.message || 'Error downloading addon', Toast.Type.DANGER, 'Download Failed');
    }
  };

  const gridTemplateColumns = getAddonTableGridTemplateColumns(settings);
  const dateToUse = archived ? archivedAt : createdAt;

  return (
    <Table.Row
      id={id}
      className={getClasses(isSelected && 'selected', focused && 'focused')}
      onDoubleClick={handleDoubleClick}
      style={{ gridTemplateColumns }}
    >
      {settings.columns.SELECT && (
        <Table.Cell className="SELECT">
          <Input type="checkbox" onClick={onSelect} checked={isSelected} />
        </Table.Cell>
      )}
      {settings.columns.DATE && (
        <Table.Cell className="DATE" data-placeholder="--">
          {dateToUse ? new Dayts(dateToUse).toLocal().format(DATETIME_FE_FORMAT_SHORT) : ''}
        </Table.Cell>
      )}
      {settings.columns.STATUS && (
        <Table.Cell className={getClasses('STATUS', status)} data-placeholder="--">
          {statusDisplayName || ''}
        </Table.Cell>
      )}
      {settings.columns.PROVIDER && (
        <Table.Cell className="PROVIDER" data-placeholder="--">
          {provider?.displayName || ''}
        </Table.Cell>
      )}
      {settings.columns.IMPORTED_BY && (
        <Table.Cell className="IMPORTED-BY" data-placeholder="--">
          {archived ? archivedByUser || '' : importedBy || ''}
        </Table.Cell>
      )}
      {settings.columns.FROM && (
        <Table.Cell className="FROM" data-placeholder="--">
          {email?.from || ''}
        </Table.Cell>
      )}
      {settings.columns.SUBJECT && (
        <Table.Cell className="SUBJECT" data-placeholder="--">
          {email?.subject || ''}
        </Table.Cell>
      )}
      {settings.columns.TYPE && (
        <Table.Cell className="TYPE" data-placeholder="--">
          {type || ''}
        </Table.Cell>
      )}
      {settings.columns.DOWNLOAD && (
        <Table.Cell className="DOWNLOAD" data-placeholder="--">
          <Tippy content="Download" placement="top">
            <Button variant="icon" onClick={onDownload}>
              <i className="sv sv-download2 fs-4" />
            </Button>
          </Tippy>
        </Table.Cell>
      )}
    </Table.Row>
  );
};

export default AddonTableRow;
